<script setup>
import { ref, onMounted, reactive, computed } from 'vue';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import { useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';

const router = useRouter();
const isLoading = ref(true);
const processingId = ref(null);
const search = ref('');

// Form validation state
const valid = ref(true);
const dialog = ref(false);
const deleteDialog = ref(false);
const serviceToDelete = ref(null);
const processing = ref(false);
const editedIndex = ref(-1);
const editedItem = reactive({
  id: null,
  name: '',
  type: 'military',
  active: true
});
const defaultItem = {
  id: null,
  name: '',
  type: 'military',
  active: true
};

// Validation rules
const nameRules = [
  v => !!v || 'Service name is required',
  v => (v && v.length <= 200) || 'Service name must be less than 200 characters'
];

// Notifications
const snackbar = reactive({
  show: false,
  text: '',
  color: 'success'
});

// Sample data - in a real application, this would come from an API
const services = ref([]);

const filteredServices = computed(() => {
  if (!search.value) {
    return services.value;
  }

  const searchTerm = search.value.toLowerCase();
  return services.value.filter(item =>
    item.name.toLowerCase().includes(searchTerm) ||
    item.type.toLowerCase().includes(searchTerm)
  );
});

// Computed values
const formTitle = computed(() => {
  return editedIndex.value === -1 ? 'New Code of Service' : 'Edit Code of Service';
});

// Methods
const fetchServices = async () => {
  isLoading.value = true;
  try {
    const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/service-types`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      }
    });

    if (!response.ok) {
      throw Error("Something went wrong!");
    }

    let res = await response.json();

    res = Array.from(res['serviceTypes']).map((item) => {
      return {
        ...item,
        active: item.is_active,
      }
    });

    services.value = res;

  } catch (error) {
    showSnackbar('Failed to load services', 'error');
    console.error('Error fetching services:', error);
  } finally {
    isLoading.value = false;
  }
};

const openAddDialog = () => {
  editedIndex.value = -1;
  editedItem.id = null;
  editedItem.name = '';
  editedItem.type = 'military';
  editedItem.active = true;
  dialog.value = true;
};

const editService = (item) => {
  editedIndex.value = services.value.findIndex(s => s.id === item.id);
  Object.assign(editedItem, JSON.parse(JSON.stringify(item)));
  dialog.value = true;
};

const closeDialog = () => {
  dialog.value = false;
  setTimeout(() => {
    Object.assign(editedItem, defaultItem);
    editedIndex.value = -1;
  }, 300);
};

const saveService = async () => {
  if (!editedItem.name) {
    showSnackbar('Please fill in all required fields', 'error');
    return;
  }

  processing.value = true;

  try {
    if (editedIndex.value === -1) {
      const nameExists = services.value.some(
        s => s.name.toLowerCase() === editedItem.name.toLowerCase()
      );

      if (nameExists) {
        showSnackbar(`Service "${editedItem.name}" already exists`, 'error');
        processing.value = false;
        return;
      }

      const newService = {
        name: editedItem.name,
        type: editedItem.type,
        isActive: editedItem.active
      };

      const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/service-types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(newService)
      });

      if (!response.ok) {
        throw Error("Something went wrong!");
      }

      let res = await response.json();
      res = res['serviceType'];
      res['active'] = res['is_active'];

      services.value.push(res);
      showSnackbar('Service added successfully', 'success');
    } else {
      const updatedService = {
        name: editedItem.name,
        type: editedItem.type,
        isActive: editedItem.active
      };
      
      const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/service-types/${editedItem.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(updatedService)
      });

      if (!response.ok) {
        throw Error("Something went wrong!");
      }

      Object.assign(services.value[editedIndex.value], editedItem);
      showSnackbar('Service updated successfully', 'success');
    }

    closeDialog();
  } catch (error) {
    showSnackbar('Operation failed', 'error');
    console.error('Error saving service:', error);
  } finally {
    processing.value = false;
  }
};

const confirmDelete = (item) => {
  serviceToDelete.value = item;
  deleteDialog.value = true;
};

const deleteService = async () => {
  processing.value = true;
  try {
    const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/service-types/${serviceToDelete.value.id}`, {
      method: "DELETE",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      },
    });

    const index = services.value.findIndex(s => s.id === serviceToDelete.value.id);
    services.value.splice(index, 1);

    showSnackbar('Service deleted successfully', 'success');
    deleteDialog.value = false;
  } catch (error) {
    showSnackbar('Failed to delete service', 'error');
    console.error('Error deleting service:', error);
  } finally {
    processing.value = false;
    serviceToDelete.value = null;
  }
};

const toggleServiceStatus = async (item) => {
  processingId.value = item.id;
  try {
    const updatedService = {
      isActive: !item.active
    };

    const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/admin/service-types/${item.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      },
      body: JSON.stringify(updatedService)
    });

    if (!response.ok) {
      throw Error("Something went wrong!");
    }
    
    item.active = !item.active;
    showSnackbar(`Service ${item.active ? 'enabled' : 'disabled'} successfully`, 'success');
  } catch (error) {
    // Revert the toggle if the API call fails
    // item.active = !item.active;
    showSnackbar('Failed to update service status', 'error');
    console.error('Error toggling service status:', error);
  } finally {
    processingId.value = null;
  }
};

const showSnackbar = (text, color = 'success') => {
  snackbar.text = text;
  snackbar.color = color;
  snackbar.show = true;

  // Or use vue3-toastify
  if (color === 'error') {
    toast?.error(text);
  } else {
    toast?.success(text);
  }
};

// Initialize on component mount
onMounted(async () => {
  // Check authentication before loading the page
  const token = localStorage.getItem('auth_token');
  const expiresAt = localStorage.getItem('expiresAt');

  // If no token or token is expired, redirect to login
  if (!token || !expiresAt || new Date(expiresAt).getTime() <= new Date().getTime()) {
    toast?.error("Authentication required");
    router.push('/login');
    return;
  }

  await fetchServices();
});
</script>

<template>
  <!-- Loading Spinner while loading data -->
  <div v-if="isLoading" class="w-full h-screen flex items-center justify-center bg-page">
    <div class="flex flex-col items-center">
      <div class="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <p class="mt-4 text-primary font-monster font-medium">Loading...</p>
    </div>
  </div>

  <!-- Main content - only shown when data is loaded -->
  <div v-else class="w-[calc(100vw-4rem)] flex-grow flex flex-col pl-6 pr-8 py-7 gap-y-8 overflow-y-scroll">
    <div class="p-2 bg-page">
      <h4 class="text-primary font-bold font-monster -ml-2">
        Code of Service Management
      </h4>
    </div>

    <div class="bg-white rounded-[14px] shadow-[2px_2px_6px_rgba(187,195,206,0.4)] p-6">
      <!-- Header with search and add button -->
      <div class="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <!-- Search field -->
        <div class="w-full md:w-1/3 relative">
          <input 
            v-model="search"
            type="text"
            placeholder="Search services..."
            class="rounded-[10px] pl-10 pr-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium placeholder:text-[85%] placeholder:text-gray-400 w-full"
          />
          <svg class="absolute left-3 top-1/2 -translate-y-1/2" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="#020754" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M21 21L16.65 16.65" stroke="#020754" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        
        <!-- Add service button -->
        <button 
          @click="openAddDialog"
          class="font-monster flex justify-center gap-2 items-center font-semibold bg-primary hover:bg-primary/60 transition-all ease-in-out duration-[750ms] rounded-[0.625rem] text-white py-3 px-4"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M5 12H19" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Add Code of Service
        </button>
      </div>

      <!-- Services table -->
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 table-fixed">
          <thead>
            <tr class="font-monster font-bold text-secondary text-left">
              <th class="py-3 px-4 w-2/5">Name</th>
              <th class="py-3 px-4 w-1/5">Type</th>
              <th class="py-3 px-4 w-1/5">Status</th>
              <th class="py-3 px-4 w-1/5">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-100">
            <tr v-for="service in filteredServices" :key="service.id" class="hover:bg-gray-50">
              <td class="py-3 px-4 font-monster font-medium">{{ service.name }}</td>
              <td class="py-3 px-4 font-monster">{{ service.type === "military" ? "Military" : "Consumer" }}</td>
              <td class="py-3 px-4">
                <div class="w-12 h-6 flex items-center relative cursor-pointer" @click="toggleServiceStatus(service)">
                  <div
                    class="w-12 h-6 rounded-full transition-colors duration-200 ease-in-out"
                    :class="service.active ? 'bg-secondary' : 'bg-gray-300'"
                  >
                    <div
                      class="w-5 h-5 bg-white rounded-full shadow absolute top-0.5 left-0.5 transition-transform duration-200 ease-in-out"
                      :class="service.active ? 'transform translate-x-6' : ''"
                      :style="processingId === service.id ? 'opacity: 0.5;' : ''"
                    ></div>
                  </div>
                </div>
              </td>
              <td class="py-3 px-4">
                <div class="flex items-center gap-2">
                  <button
                    @click="editService(service)"
                    class="text-secondary hover:text-primary transition-colors duration-200"
                    :disabled="processingId === service.id"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                  <button
                    @click="confirmDelete(service)"
                    class="text-primary hover:text-red-700 transition-colors duration-200"
                    :disabled="processingId === service.id"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6H5H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="filteredServices.length === 0">
              <td colspan="4" class="py-6 text-center text-gray-500 font-monster">
                No code of services found
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Add/Edit Dialog -->
    <div v-if="dialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <h2 class="text-xl font-monster font-bold text-secondary mb-4">{{ formTitle }}</h2>
        
        <div class="mb-4">
          <label class="font-monster text-secondary block mb-1">
            Code of Service Name *
          </label>
          <input
            v-model="editedItem.name"
            class="rounded-[10px] px-4 py-3 bg-white border-[1px] border-stroke font-monster border-solid outline-none font-medium w-full"
            placeholder="Enter code of service"
          />
          <p v-if="editedItem.name && editedItem.name.length > 200" class="text-sm text-primary mt-1">
            Name must be less than 200 characters
          </p>
        </div>
        
        <div class="mb-4">
          <label class="font-monster text-secondary block mb-1">
            Service Type *
          </label>
          <div class="flex items-center space-x-6 mt-1 py-1">
            <label class="inline-flex items-center cursor-pointer not-required relative">
              <input 
                type="radio" 
                name="service-type" 
                id="service-type-military" 
                value="military"
                v-model="editedItem.type" 
                class="hidden peer" 
                checked 
              />
              <span class="w-4 h-4 border border-secondary rounded-full flex items-center justify-center mr-1.5 peer-checked:bg-white peer-checked:border-secondary">
              </span>
              <span class="absolute w-2 h-2 left-[4px] top-[6px] rounded-full bg-secondary opacity-0 peer-checked:opacity-100"></span>
              <span class="text-sm text-secondary">Military</span>
            </label>
            <label class="inline-flex items-center cursor-pointer not-required relative">
              <input 
                type="radio" 
                name="service-type" 
                id="service-type-consumer" 
                value="consumer"
                v-model="editedItem.type" 
                class="hidden peer" 
              />
              <span class="w-4 h-4 border border-secondary rounded-full flex items-center justify-center mr-1.5 peer-checked:bg-white peer-checked:border-secondary">
              </span>
              <span class="absolute w-2 h-2 left-[4px] top-[6px] rounded-full bg-secondary opacity-0 peer-checked:opacity-100"></span>
              <span class="text-sm text-secondary">Consumer</span>
            </label>
          </div>
        </div>
        
        <div class="mb-6 flex items-center">
          <div class="w-12 h-6 flex items-center relative cursor-pointer mr-2" @click="editedItem.active = !editedItem.active">
            <div
              class="w-12 h-6 rounded-full transition-colors duration-200 ease-in-out"
              :class="editedItem.active ? 'bg-secondary' : 'bg-gray-300'"
            >
              <div
                class="w-5 h-5 bg-white rounded-full shadow absolute top-0.5 left-0.5 transition-transform duration-200 ease-in-out"
                :class="editedItem.active ? 'transform translate-x-6' : ''"
              ></div>
            </div>
          </div>
          <label class="font-monster text-secondary">Active</label>
        </div>
        
        <div class="flex justify-end gap-3">
          <button
            @click="closeDialog"
            class="font-monster py-2 px-4 rounded-[0.625rem] border border-stroke text-secondary hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="saveService"
            :disabled="!editedItem.name || processing || editedItem.name.length > 200"
            class="font-monster py-2 px-4 rounded-[0.625rem] bg-primary text-white hover:bg-primary/80 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg v-if="processing" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Save
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <div v-if="deleteDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <h2 class="text-xl font-monster font-bold text-secondary mb-2">Delete Code of `Service</h2>
        <p class="mb-6 text-gray-600">
          Are you sure you want to delete this service? This action cannot be undone.
        </p>
        
        <div v-if="serviceToDelete" class="mb-6 p-3 bg-gray-50 rounded-lg">
          <p class="font-monster"><span class="font-bold text-secondary">Name:</span> {{ serviceToDelete.name }}</p>
          <p class="font-monster"><span class="font-bold text-secondary">Type:</span> {{ serviceToDelete.type === "military" ? "Military" : "Consumer" }}</p>
        </div>
        
        <div class="flex justify-end gap-3">
          <button
            @click="deleteDialog = false"
            class="font-monster py-2 px-4 rounded-[0.625rem] border border-stroke text-secondary hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="deleteService"
            :disabled="processing"
            class="font-monster py-2 px-4 rounded-[0.625rem] bg-primary text-white hover:bg-primary/80 transition-colors flex items-center gap-2"
          >
            <svg v-if="processing" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom style for the switch when disabled/processing */
button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
